import type { ReactNode } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

export const PageContainer = ({
  className,
  variant = "document",
  ...otherProps
}: React.ComponentProps<typeof Stack> & {
  variant?: "document" | "workspace";
}) => (
  <Stack
    gap={6}
    align="stretch"
    className={cn(
      "w-full",
      "mx-auto max-w-[1680px] px-4 pt-6 pb-8 md:px-8",
      variant === "workspace" ? "h-page min-h-0" : "min-h-page",
      className,
    )}
    {...otherProps}
  />
);

export const PageTitle = ({
  className,
  ...otherProps
}: React.ComponentProps<"h1">) => (
  <h1
    className={cn(
      "text-4xl leading-[1.1] font-medium tracking-[-0.02em] text-balance text-ink [&_[data-slot=badge]]:tracking-normal",
      className,
    )}
    {...otherProps}
  />
);

export const PageHeader = ({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <Stack
    direction="row"
    justify="between"
    align="center"
    gap={4}
    className={cn("w-full", "flex-wrap gap-y-2", className)}
  >
    <Stack align="start" gap={1} className="min-w-0">
      <PageTitle>{title}</PageTitle>
      {description && (
        <p className="text-sm leading-[1.5] text-pretty text-subtle">
          {description}
        </p>
      )}
    </Stack>
    {actions && (
      <Stack
        direction="row"
        align="center"
        gap={2}
        className="flex-wrap max-sm:*:min-h-9 max-sm:[&>.size-8]:size-9"
      >
        {actions}
      </Stack>
    )}
  </Stack>
);

export const PageMainCard = ({
  className,
  ...otherProps
}: React.ComponentProps<typeof Card>) => (
  <Card
    className={cn(
      "flex min-h-0 w-full flex-1 flex-col gap-0 overflow-hidden py-0",
      className,
    )}
    {...otherProps}
  />
);

export const PageMainStack = ({
  className,
  ...otherProps
}: React.ComponentProps<typeof Stack>) => (
  <Stack
    gap={4}
    align="start"
    className={cn("w-full", "h-full overflow-hidden px-6", className)}
    {...otherProps}
  />
);
